import { api, setAccessToken, setCsrfToken } from '@/core/api/client';
import type { AdminRole, PermissionResponse } from './types';

/** Backend login returns `user` (not `profile`) — see admin-auth.service.ts. */
export interface LoginUser {
  id?: string;
  email?: string;
  phone?: string;
  role?: AdminRole;
  tenant_id?: string | null;
}

export interface LoginResponse {
  access_token?: string;
  refresh_token?: string;
  user?: LoginUser;
}

/** Backend login DTO is { email?, phone?, otp?, password?, msg91_access_token? }
 *  (apps/admin-api/src/admin/dto/admin-login.dto.ts). Map the single UI
 *  identifier field to email vs phone. */
function identifierField(identifier: string): { email: string } | { phone: string } {
  const v = identifier.trim();
  return v.includes('@') ? { email: v } : { phone: v };
}

// Endpoint contract:
// - Password: POST /admin/auth/login { email|phone, password }
// - Email OTP: step 1 POST { email } (sends OTP) -> step 2 POST { email, otp }
// - /csrf, /refresh, /logout are Phase 0b backend additions (see PHASE_0B_BACKEND.md).
//   Web model: access token in memory (Bearer), refresh token in httpOnly cookie.
export const authApi = {
  async csrf(): Promise<void> {
    try {
      const r = await api.get<{ csrfToken?: string }>('/admin/auth/csrf', { skipAuthRetry: true });
      setCsrfToken(r?.csrfToken ?? null);
    } catch {
      setCsrfToken(null); // endpoint lands in Phase 0b
    }
  },
  async loginPassword(identifier: string, password: string): Promise<LoginResponse> {
    const r = await api.post<LoginResponse>(
      '/admin/auth/login',
      { ...identifierField(identifier), password },
      { skipAuthRetry: true },
    );
    if (r?.access_token) setAccessToken(r.access_token);
    return r;
  },
  /** Email-OTP step 1: send the code (identifier only). */
  requestOtp(identifier: string) {
    return api.post<unknown>('/admin/auth/login', { ...identifierField(identifier) }, { skipAuthRetry: true });
  },
  /** Email-OTP step 2: verify + issue tokens. */
  async verifyOtp(identifier: string, otp: string): Promise<LoginResponse> {
    const r = await api.post<LoginResponse>(
      '/admin/auth/login',
      { ...identifierField(identifier), otp },
      { skipAuthRetry: true },
    );
    if (r?.access_token) setAccessToken(r.access_token);
    return r;
  },
  permissions() {
    return api.get<PermissionResponse>('/admin/auth/permissions');
  },
  async refresh(): Promise<LoginResponse> {
    const r = await api.post<LoginResponse>('/admin/auth/refresh', undefined, { skipAuthRetry: true });
    if (r?.access_token) setAccessToken(r.access_token);
    return r;
  },
  async logout(): Promise<void> {
    try {
      await api.post('/admin/auth/logout', undefined, { skipAuthRetry: true });
    } finally {
      setAccessToken(null);
      setCsrfToken(null);
    }
  },
};
